// @ts-nocheck — vendored/mirrored file: logic is byte-faithful to popin-rn and
// verified in its home repo's CI. The portal's tsconfig is stricter
// (noUncheckedIndexedAccess) than upstream's, which flags guarded indexed access
// as false positives; the engine boundary is type-checked via ./derive.ts.
// ═══════════════════════════════════════════════════════════════════════════
// MIRRORED FROM popin-rn — DO NOT EDIT BY HAND.
//   source: smiletime/popin-rn  design/ds-sync/engine/anchor-generate.ts
//   ref:    65f24d55aa9d4f5e978ff6697da875767f6cb8b2  (origin/main, fetched 2026-07-03)
// Byte-for-byte copy of the pop.in Theme Studio engine (PURE — no react-native).
// The portal generates the SAME palettes the app renders. Drift is tracked by the
// source git SHA in ./provenance.ts (MIRRORED_FROM_SHA); re-sync via
// scripts/resync-theme-engine.sh. See ./README.md.
// ═══════════════════════════════════════════════════════════════════════════
/**
 * anchor-generate — the SHOWCASE anchor-and-generate theming engine (de-risk spike).
 *
 * ⚠️ THIS IS NOT THE LIVE ENGINE. The shipped runtime is `utils/brandTheme.ts`
 *    (Sam's PR #311 — a naive HSL luminance LERP, dark-only, no per-step
 *    contrast guarantee). This module is a *proof* of the upgrade the partner-
 *    portal PLAN ("THE THEMING ENGINE") calls for: pin a brand seed at the
 *    identity step and GENERATE the other 11 steps for contrast IN OKLCH, for
 *    light AND dark, deriving the on-color and gating on WCAG 2.2 AA. It emits
 *    the SAME semantic-token shape as `buildBrandTheme` so it drops straight
 *    into `withSemanticTokens` / the `<Theme>` cascade via `updateTheme`.
 *
 * No npm deps (Radix's `generateRadixColors` needs colorjs.io + bezier-easing,
 * neither installed; `culori` also absent). We hand-roll Björn Ottosson's
 * OKLab/OKLCH transforms — the sanctioned "minimal OKLCH equivalent" fallback
 * from the runbook. Byte-exact Radix scales can be vendored later if AJ wants
 * them; the architecture this proves is identical.
 *
 * Runnable directly in Node 22 (`node --experimental-strip-types …`) for the
 * accessibility de-risk, AND importable by the Metro showcase route.
 */

export type Appearance = "light" | "dark";
export interface OKLCH { L: number; C: number; H: number } // L 0..1, C 0..~0.4, H degrees
export interface RGB { r: number; g: number; b: number } // each 0..1

// ─────────────────────────────────────────────────────────────────────────
// sRGB ⇄ linear ⇄ OKLab ⇄ OKLCH  (Ottosson 2020; matrices verbatim)
// ─────────────────────────────────────────────────────────────────────────
const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);
const cbrt = (n: number) => Math.cbrt(n);

function srgbToLinear(c: number): number {
	return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
function linearToSrgb(c: number): number {
	return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

function linRgbToOklab(r: number, g: number, b: number): { L: number; a: number; bb: number } {
	const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
	const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
	const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
	const l_ = cbrt(l), m_ = cbrt(m), s_ = cbrt(s);
	return {
		L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
		a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
		bb: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
	};
}
function oklabToLinRgb(L: number, a: number, b: number): RGB {
	const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
	const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
	const s_ = L - 0.0894841775 * a - 1.291485548 * b;
	const l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
	return {
		r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
		g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
		b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
	};
}

const inGamut = (c: RGB) =>
	c.r >= -1e-4 && c.r <= 1.0001 && c.g >= -1e-4 && c.g <= 1.0001 && c.b >= -1e-4 && c.b <= 1.0001;

/** OKLCH → in-gamut sRGB. If the requested chroma falls outside sRGB, reduce C
 *  (holding L and H — "hold the hue, slide the chroma") via binary search until
 *  it fits. This is how a vivid seed at an impossible step still yields a clean
 *  swatch instead of a clipped, hue-shifted mess. */
function oklchToRgb(o: OKLCH): RGB {
	const hr = (o.H * Math.PI) / 180;
	const toRgb = (C: number) => oklabToLinRgb(o.L, C * Math.cos(hr), C * Math.sin(hr));
	let lin = toRgb(o.C);
	if (!inGamut(lin)) {
		// Gamut bounds are identical in linear and sRGB, so reduce chroma on the
		// LINEAR values (hold L and H) until they fit.
		let lo = 0, hi = o.C;
		for (let i = 0; i < 24; i++) {
			const mid = (lo + hi) / 2;
			if (inGamut(toRgb(mid))) lo = mid; else hi = mid;
		}
		lin = toRgb(lo);
	}
	// oklabToLinRgb yields LINEAR sRGB — gamma-encode before returning display sRGB.
	return {
		r: clamp01(linearToSrgb(clamp01(lin.r))),
		g: clamp01(linearToSrgb(clamp01(lin.g))),
		b: clamp01(linearToSrgb(clamp01(lin.b))),
	};
}

// ─────────────────────────────────────────────────────────────────────────
// hex ⇄ OKLCH
// ─────────────────────────────────────────────────────────────────────────
export function hexToRgb(hex: string): RGB {
	let h = hex.trim().replace(/^#/, "");
	if (h.length === 3) h = h.split("").map((c) => c + c).join("");
	return {
		r: parseInt(h.slice(0, 2), 16) / 255,
		g: parseInt(h.slice(2, 4), 16) / 255,
		b: parseInt(h.slice(4, 6), 16) / 255,
	};
}
function rgbToHex(c: RGB): string {
	const to = (n: number) => clamp01(n) * 255;
	const hx = (n: number) => Math.round(to(n)).toString(16).padStart(2, "0");
	return `#${hx(c.r)}${hx(c.g)}${hx(c.b)}`;
}
export function hexToOklch(hex: string): OKLCH {
	const { r, g, b } = hexToRgb(hex);
	const lab = linRgbToOklab(srgbToLinear(r), srgbToLinear(g), srgbToLinear(b));
	const C = Math.sqrt(lab.a * lab.a + lab.bb * lab.bb);
	let H = (Math.atan2(lab.bb, lab.a) * 180) / Math.PI;
	if (H < 0) H += 360;
	return { L: lab.L, C, H };
}
export const oklchToHex = (o: OKLCH): string => rgbToHex(oklchToRgb(o));

// ─────────────────────────────────────────────────────────────────────────
// Contrast — WCAG 2.2 (gate) + APCA 0.1.9 Lc (advisory)
// ─────────────────────────────────────────────────────────────────────────
function relLuminance(hex: string): number {
	const { r, g, b } = hexToRgb(hex);
	const lin = (c: number) => srgbToLinear(c);
	return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
/** WCAG 2.2 contrast ratio (1..21). */
export function wcagContrast(fg: string, bg: string): number {
	const L1 = relLuminance(fg), L2 = relLuminance(bg);
	const hi = Math.max(L1, L2), lo = Math.min(L1, L2);
	return (hi + 0.05) / (lo + 0.05);
}
/** APCA 0.1.9 lightness contrast (Lc, signed; advisory for dark pairs where
 *  WCAG2 flatters light-on-dark). Magnitude ~ perceptual contrast. */
export function apcaLc(text: string, bg: string): number {
	const Y = (hex: string) => {
		const { r, g, b } = hexToRgb(hex);
		return 0.2126729 * Math.pow(r, 2.4) + 0.7151522 * Math.pow(g, 2.4) + 0.072175 * Math.pow(b, 2.4);
	};
	let Ytxt = Y(text), Ybg = Y(bg);
	const blkThrs = 0.022, blkClmp = 1.414;
	const clampBlk = (y: number) => (y < blkThrs ? y + Math.pow(blkThrs - y, blkClmp) : y);
	Ytxt = clampBlk(Ytxt); Ybg = clampBlk(Ybg);
	if (Math.abs(Ybg - Ytxt) < 0.0005) return 0;
	let Lc: number;
	if (Ybg > Ytxt) {
		Lc = (Math.pow(Ybg, 0.56) - Math.pow(Ytxt, 0.57)) * 1.14;
		Lc = Lc < 0.001 ? 0 : Lc - 0.027;
	} else {
		Lc = (Math.pow(Ybg, 0.65) - Math.pow(Ytxt, 0.62)) * 1.14;
		Lc = Lc > -0.001 ? 0 : Lc + 0.027;
	}
	return Lc * 100;
}

// ─────────────────────────────────────────────────────────────────────────
// The recipe — anchor at step 9, generate the rest in OKLCH
// ─────────────────────────────────────────────────────────────────────────

// Perceptual lightness anchors for steps 1..8 (surfaces) per polarity. Step 9
// = the seed verbatim; 10..12 derived below. Eyeballed off Radix's gray/accent
// L distribution — monotone so contrast rises by construction.
const SURFACE_L: Record<Appearance, number[]> = {
	dark:  [0.178, 0.213, 0.248, 0.276, 0.303, 0.339, 0.402, 0.476],
	light: [0.9950, 0.9830, 0.9640, 0.9430, 0.9220, 0.8960, 0.8600, 0.8060],
};

/** Bright-hue band (OKLCH hue°) where a saturated step-9 is light enough that a
 *  DARK label reads better — Sky/Mint/Lime/Yellow/Amber. Carries the runbook's
 *  "bright-hue exception" so we never slap white on a yellow button. */
function isBrightHue(H: number): boolean {
	return (H >= 60 && H <= 200);
}

export interface Ramp { hex: string[]; onColor: string; seedAdjusted: boolean; appearance: Appearance; seed: string }

/** Generate the 12-step ramp (index 1..12; index 0 unused) + on-color. */
export function generateRamp(seed: string, appearance: Appearance): Ramp {
	const s = hexToOklch(seed);
	const dark = appearance === "dark";
	const surfaceL = SURFACE_L[appearance];
	const hex: string[] = new Array(13).fill("#000000");

	// Steps 1..8 — accent-tinted neutral surfaces. Low chroma so chrome reads as
	// a tinted gray; tint rises slightly toward 8. Hue locked to the seed.
	for (let i = 1; i <= 8; i++) {
		const tintCap = dark ? 0.006 + 0.006 * i : 0.004 + 0.005 * i;
		hex[i] = oklchToHex({ L: surfaceL[i - 1], C: Math.min(s.C, tintCap), H: s.H });
	}

	// Step 9 — THE ANCHOR. Seed verbatim (identity). Adjusted only if it cannot
	// carry a readable label (handled after on-color below).
	let step9: OKLCH = { ...s };

	// On-color: pick the polarity-correct label, max WCAG vs the seed. Bright
	// hues bias dark. If neither hits AA, slide step-9 L (hold H, ease C) until
	// the better label clears 4.5 — "a dark yellow is no longer yellow".
	const labelCandidates = isBrightHue(s.H)
		? ["#0B0B0C", "#FAFAFA"] // try dark first for bright hues
		: ["#FAFAFA", "#0B0B0C"];
	let seedAdjusted = false;
	const pickOn = (nineHex: string) => {
		const a = wcagContrast(labelCandidates[0], nineHex);
		const b = wcagContrast(labelCandidates[1], nineHex);
		return a >= b ? { on: labelCandidates[0], cr: a } : { on: labelCandidates[1], cr: b };
	};
	let nineHex = oklchToHex(step9);
	let chosen = pickOn(nineHex);
	if (chosen.cr < 4.5) {
		// Slide L toward the direction that helps the winning label, holding hue.
		const wantDarker = chosen.on === labelCandidates[0] ? isBrightHue(s.H) : false;
		// Simpler: search L across the full range for the best achievable label CR.
		let best = chosen, bestL = step9.L;
		for (let L = 0.30; L <= 0.92; L += 0.01) {
			const cand = oklchToHex({ L, C: step9.C, H: step9.H });
			const p = pickOn(cand);
			if (p.cr > best.cr) { best = p; bestL = L; }
			if (p.cr >= 4.6) { best = p; bestL = L; break; }
		}
		step9 = { ...step9, L: bestL };
		nineHex = oklchToHex(step9);
		chosen = pickOn(nineHex);
		seedAdjusted = true;
		void wantDarker;
	}
	hex[9] = nineHex;
	const onColor = chosen.on;

	// Step 10 — solid hover. Dark → lighter; light → darker. Hold hue/chroma.
	hex[10] = oklchToHex({ L: clamp01(step9.L + (dark ? 0.04 : -0.05)), C: step9.C, H: step9.H });

	// Steps 11 (low-contrast text) + 12 (high-contrast text) — accent-tinted,
	// tuned so BOTH clear AA (≥4.5) against the step-1 background by construction.
	const bg = hex[1];
	const tune = (startL: number, chroma: number, target: number): string => {
		let L = startL;
		for (let i = 0; i < 80; i++) {
			const cand = oklchToHex({ L, C: chroma, H: s.H });
			if (wcagContrast(cand, bg) >= target) return cand;
			L += dark ? 0.008 : -0.008; // dark→lighten text, light→darken text
			if (L <= 0.02 || L >= 0.99) return cand;
		}
		return oklchToHex({ L, C: chroma, H: s.H });
	};
	hex[11] = tune(dark ? 0.72 : 0.55, Math.min(s.C * 0.55, 0.10), 4.5);
	hex[12] = tune(dark ? 0.92 : 0.26, Math.min(s.C * 0.14, 0.022), 7.0);

	return { hex, onColor, seedAdjusted, appearance, seed };
}

// ─────────────────────────────────────────────────────────────────────────
// Map the ramp into the buildBrandTheme semantic-token shape
// ─────────────────────────────────────────────────────────────────────────
const withAlpha = (hex: string, a: number): string => {
	const { r, g, b } = hexToRgb(hex);
	return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
};

/**
 * buildEngineTheme — emit the full ~46-key override object `updateTheme` wants,
 * mirroring `utils/brandTheme.ts buildBrandTheme` key-for-key, but sourced from
 * the OKLCH anchor-and-generate ramp (color9 = the brand seed). HOST-LOCKED
 * neutrals/states map to ramp steps exactly as the live module does.
 */
export function buildEngineTheme(seed: string, appearance: Appearance): Record<string, string> {
	const ramp = generateRamp(seed, appearance);
	const c = (i: number) => ramp.hex[i];
	const primary = c(9);
	const onPrimary = ramp.onColor;

	return {
		// ── Base Tamagui keys (generic Input/Stack/Text defaults) ──
		color0: withAlpha(c(12), 0),
		color1: c(1), color2: c(2), color3: c(3), color4: c(4),
		color5: c(5), color6: c(6), color7: c(7), color8: c(8),
		color9: c(9), color10: c(10), color11: c(11), color12: c(12),
		background: c(1),
		backgroundHover: c(2),
		backgroundPress: c(3),
		backgroundFocus: c(2),
		borderColor: c(6),
		borderColorHover: c(7),
		borderColorPress: c(5),
		borderColorFocus: c(8),
		color: c(12),
		colorHover: c(11),
		colorPress: c(12),
		colorFocus: c(11),
		placeholderColor: c(8),
		accentBackground: primary,
		accentColor: onPrimary,

		// ── SEMANTIC MAP — mirrors tamagui.config.ts withSemanticTokens ──
		actionPrimary: primary,
		actionPrimaryGlow: withAlpha(primary, 0.4),
		actionOnPrimary: onPrimary,
		actionSecondaryBorder: c(6),
		actionOnSecondary: c(11),
		actionTertiary: c(7),
		actionOnTertiary: c(12),
		stateScheduled: c(8),
		stateOnScheduled: c(12),
		stateEnded: c(6),
		stateOnEnded: c(11),
		stateUnread: withAlpha(primary, 0.1),
		stateRead: c(7),
		surfaceBackground: c(1),
		surfaceMuted: c(2),
		surfaceCard: c(3),
		surfaceElevated: c(4),
		textDefault: c(12),
		textSecondary: c(11),
		textMuted: c(8),
		textDisabled: c(6),
		borderDefault: c(6),
		borderFocus: primary,
		borderSubtle: c(4),

		// Brand chrome extras
		headerBackground: c(1),
		brandAccent: primary,
		brandAccentGlow: withAlpha(primary, 0.35),
		// #325 B3: PLAY-button "special" accent (defaults to color11).
		playAccent: c(11),
	};
}

// ─────────────────────────────────────────────────────────────────────────
// Accessibility report (the numbers the runbook asks us to print)
// ─────────────────────────────────────────────────────────────────────────
export interface A11yPair { name: string; fg: string; bg: string; wcag: number; apca: number; floor: number; pass: boolean }
export interface ThemeReport {
	label: string; seed: string; appearance: Appearance; seedAdjusted: boolean;
	onColor: string; ramp: string[]; pairs: A11yPair[]; info: A11yPair[]; allPass: boolean;
}

export function themeReport(label: string, seed: string, appearance: Appearance): ThemeReport {
	const ramp = generateRamp(seed, appearance);
	const t = buildEngineTheme(seed, appearance);
	const mk = (name: string, fg: string, bg: string, floor: number): A11yPair => {
		const wcag = wcagContrast(fg, bg);
		return { name, fg, bg, wcag: Math.round(wcag * 100) / 100, apca: Math.round(apcaLc(fg, bg) * 10) / 10, floor, pass: wcag >= floor };
	};
	// WCAG 2.2 gate. 1.4.3 (text) → ≥4.5. 1.4.11 (non-text) → ≥3 for the focus
	// indicator + the solid control's boundary. Subtle separators (steps 6–8)
	// are decorative/incidental and EXEMPT from 1.4.11 — the live dark_purple
	// keeps them under 3:1 too — so they are reported, not gated.
	const pairs: A11yPair[] = [
		mk("textDefault on bg", t.textDefault, t.surfaceBackground, 4.5),
		mk("textSecondary on bg", t.textSecondary, t.surfaceBackground, 4.5),
		mk("textDefault on card", t.textDefault, t.surfaceCard, 4.5),
		mk("onPrimary on actionPrimary", t.actionOnPrimary, t.actionPrimary, 4.5),
		mk("actionPrimary on bg (solid UI ≥3)", t.actionPrimary, t.surfaceBackground, 3.0),
		mk("borderFocus on bg (focus ring ≥3)", t.borderFocus, t.surfaceBackground, 3.0),
	];
	// Informational only (NOT part of allPass) — decorative/incidental tokens
	// that WCAG 1.4.11 exempts, surfaced for transparency.
	const info: A11yPair[] = [
		mk("textMuted on bg (caption/incidental)", t.textMuted, t.surfaceBackground, 4.5),
		mk("borderDefault on bg (subtle separator)", t.borderDefault, t.surfaceBackground, 3.0),
	];
	return {
		label, seed, appearance, seedAdjusted: ramp.seedAdjusted, onColor: ramp.onColor,
		ramp: ramp.hex.slice(1), pairs, info, allPass: pairs.every((p) => p.pass),
	};
}
